import type {
  ProductSetupConfig,
  SetupTask,
  SetupTaskGroup,
} from "../../registry/types";
import {
  isAiAutomationEnabled,
  isDisputesBillingEnabled,
} from "./disputes-settings-accessors";
import { DISPUTES_INTAKE_QUESTIONS } from "./intake";

/**
 * Disputes/Reclaim setup tasks.
 *
 * Two sequential tasks — AI automation first, then billing. Evidence
 * window stays at the system default (5 days) for the ~80% of merchants
 * it works for; merchants with long fulfillment cycles can tune it on
 * the settings page. Keeping it out of the checklist avoids a click for
 * almost everyone.
 *
 *   AI automation → billing
 *
 * `disputes/enabled` itself is no longer a task — intake submit flips
 * it (see setup-guide-host), so by the time the widget renders the
 * legacy "Set up Reclaim" predicate is already satisfied.
 */

const ENABLE_AI_AUTOMATION: SetupTask = {
  id: "enable-ai-automation",
  title: "Turn on AI evidence automation",
  description:
    "Let Reclaim AI generate and submit evidence on your behalf, automatically.",
  route: (teamId) => `/stores/${teamId}/finance/disputes/settings`,
  completionPredicate: isAiAutomationEnabled,
  requiredForLive: true,
  contextualModal: {
    title: "Turn on AI evidence automation",
    body: "Reclaim AI generates dispute evidence automatically — only billed when we win. You can switch it off any time.",
    ctaText: "Open evidence settings",
  },
};

const SET_UP_BILLING: SetupTask = {
  id: "set-up-billing",
  title: "Set up billing",
  description:
    "Accept billing through Shopify so Reclaim can charge a 25% success fee on disputes we win.",
  route: (teamId) => `/stores/${teamId}/finance/disputes/integrations`,
  completionPredicate: isDisputesBillingEnabled,
  prerequisites: [ENABLE_AI_AUTOMATION.id],
  requiredForLive: true,
  contextualModal: {
    title: "Set up billing",
    body: "Reclaim works on a 25%-on-win success fee — you only pay when we recover a chargeback. Billing accept happens through Shopify.",
    ctaText: "Open billing",
  },
};

const TASK_LIST = [ENABLE_AI_AUTOMATION, SET_UP_BILLING];

// Single ordered group — widget renders it as a flat sequential list.
const GROUPS: SetupTaskGroup[] = [
  { id: "setup", title: "Setup", taskIds: TASK_LIST.map((t) => t.id) },
];

export const disputesProductConfig: ProductSetupConfig = {
  product: "disputes",
  displayName: "Reclaim setup",
  routePattern: /\/finance\/disputes(\/|$)/,
  resumeRoute: (storeId) => `/stores/${storeId}/finance/disputes/dashboard`,
  onIntakeSubmit: async ({ rpcClient }) => {
    // Flips settings.disputes.enabled so the gate stops rendering the
    // marketing page and the merchant lands on the actual dashboard.
    // Independent of billing — billing is its own task in the checklist.
    await rpcClient.enableDisputes({});
  },
  intake: {
    questions: DISPUTES_INTAKE_QUESTIONS,
    welcome: {
      title: "Welcome to Reclaim",
      subtitle: "Let's defend your chargebacks automatically.",
      useDisputeStatsSubtitle: true,
    },
    dismissible: false,
  },
  groups: GROUPS,
  tasks: Object.fromEntries(TASK_LIST.map((t) => [t.id, t])),
  completion: {
    title: "Reclaim is live",
    body: "We're now defending your chargebacks automatically. You'll see updates as new disputes come in.",
    ctaText: "Got it",
  },
  // Predicates only read team.settings.disputes — memoize on that slice.
  relevantTeamSlice: (team) => team.settings?.disputes,
};
